// Video Director step ④ — generate + frame-precise edit (spec §6, §9.4).
// The filmstrip IS IdeaTimeline: TrackClip / Ruler / PlayheadLine / VideoThumb on
// IdeaTimeline's own classes. TrackClip is props-only and shows a menu item only
// when it receives that callback, so onSplit / onTrimHead / onDelete expose
// exactly Split at playhead, Cut & delete and Remove. Every edit leaves this
// file as a pure op over the EDL (./edl.js); VideoDirector persists it.

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import i18n from "i18next";
import {
  LuArrowRightToLine,
  LuBlend,
  LuCheck,
  LuChevronLeft,
  LuChevronRight,
  LuClapperboard,
  LuFlag,
  LuImage,
  LuImagePlus,
  LuMinus,
  LuMusic,
  LuPause,
  LuPenLine,
  LuPlay,
  LuPlus,
  LuRedo2,
  LuRefreshCw,
  LuScissors,
  LuScissorsLineDashed,
  LuSparkles,
  LuTrash2,
  LuTriangleAlert,
  LuUndo2,
  LuX,
  LuVolume2,
  LuVolumeX,
  LuWand,
  LuWandSparkles,
  LuZoomIn,
  LuZoomOut,
} from "react-icons/lu";
import TrackClip from "../../../timeline/IdeaTimeline/components/TrackClip";
import Ruler from "../../../timeline/IdeaTimeline/components/Ruler";
import { PlayheadLine } from "../../../timeline/IdeaTimeline/components/Playhead";
import VideoThumb from "../../../timeline/IdeaTimeline/components/VideoThumb";
import "../../../timeline/IdeaTimeline/index.scss";
import "../../../timeline/IdeaTimeline/components/Toolbar/Toolbar.scss";
import { StartOptions } from "./DirectorLd";
import {
  ActionMenu,
  ErrorAlert,
  FinalFramePanel,
  InsertPanel,
  MENU_Z,
  ModelPick,
  OverlayPanel,
  PolicyChip,
  RefImage,
  RefStrip,
  effectiveModel,
  nudgeBtn,
  priced,
  useDraft,
} from "./DirectorPanels";
import DirectorFrameAgent from "./DirectorFrameAgent";
import StoryAssetsPanel from "./DirectorStory";
import { PreviewButton, PreviewThumb } from "./ImagePreview";
import { AddShotPanel, NewShotCard } from "./DirectorShots";
import LibraryPicker from "./LibraryPicker";
import { SlotPicker } from "./FrameSlot";
import {
  FPS,
  MAX_REFS,
  MAX_SEGMENTS,
  MAX_TOTAL_FRAMES,
  TAKE_SEC,
  clipLevel,
  cutAfter,
  deleteSegment,
  effectiveTransition,
  dropIndex,
  isContinueShot,
  isImportShot,
  isStale,
  locate,
  moveSegment,
  newSegId,
  removeRange,
  segmentStart,
  selectTake,
  setEffect,
  splitAt,
  totalFrames,
  trimSegment,
} from "./edl";
import { CutVideos, useCutPlayer } from "./useCutPlayer";

const DEFAULT_PPS = 60; // px per second until the container has a width (and in jsdom)
const RULER_LABEL_W = 56; // px a "00:00.00" ruler label needs
const PAD = 40; // trailing px after the last frame (totalWidth = clips + PAD)
const MAX_ZOOM = 8; // × the fit scale; 1 = the whole EDL fits
const ZOOM_STEP = 1.5;
const CLIP_COLOR = "#3d7eff"; // IdeaTimeline TRACK_COLORS.video
const GENERATED_COLOR = "#8b5cf6"; // continue / inserted segments
const MAJOR_STEPS = [1, 2, 5, 10, 15, 30]; // ruler label spacing in seconds
const MAX_TAKES = 6; // spec §10
const MAX_REGEN_REFS = 4; // refIds on a keyframe regenerate

const t = (key, opts) => i18n.t(`FileModal.APIProviders.VideoGeneration.Director.${key}`, opts);
const tf = (key) => i18n.t(`FileModal.APIProviders.VideoGeneration.Director.FrameAgent.${key}`);
const tv = (key) => i18n.t(`FileModal.APIProviders.VideoGeneration.${key}`);

const frameLabelStyle = {
  fontSize: "13px",
  fontWeight: 600,
  color: "#334155",
  marginBottom: "6px",
};

// VideoStoryboardPanel's input look.
const inputStyle = {
  width: "100%",
  padding: "8px 12px",
  fontSize: "13px",
  border: "1px solid #e2e8f0",
  borderRadius: "6px",
  background: "#fff",
  color: "#1f2937",
  transition: "border-color 0.2s, box-shadow 0.2s",
  lineHeight: "1.5",
  resize: "vertical",
};

// IdeaTimeline's formatTime, at the Director's 24 fps.
function formatTime(frame) {
  const total = frame / FPS;
  const m = Math.floor(total / 60);
  const s = Math.floor(total % 60);
  const cs = Math.floor((total % 1) * 100);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}.${String(cs).padStart(2, "0")}`;
}

const isLivePending = (item) => item?.status === "pending" && !isStale(item);
// Server R15: only ready or live-pending takes count toward the cap.
const usedTakes = (s) => s.takes.filter((tk) => tk.status === "ready" || isLivePending(tk)).length;
// Server answers 400 keyframe.not_approved / 409 busy otherwise. A continue shot (R30) has no keyframe: the server re-runs the continue.
const canGenerate = (shot) =>
  !shot.takes.some(isLivePending) &&
  (isContinueShot(shot) ||
    shot.startMode === "none" ||
    (shot.keyframe?.status === "ready" && (!!shot.keyframe.approved || shot.startMode === "ref")));
// Server edl.full: no frame budget for one more take (192 frames, one lost to the cut frame) or 200 segments.
const edlFull = (edl) =>
  totalFrames(edl) + (TAKE_SEC * FPS - 1) > MAX_TOTAL_FRAMES || edl.length >= MAX_SEGMENTS;
const TAKE_ERRORS = [
  "content_filtered",
  "import_too_large",
  "import_too_long",
  "end_frame_unsupported",
];
const takeError = (tk) =>
  t(TAKE_ERRORS.includes(tk.error) ? `Errors.${tk.error}` : "Errors.generic");
const takeName = (tk, n) =>
  tk.source === "import"
    ? t("Imported")
    : `${t("Take", { n: n + 1 })}${tk.finalizeOrphan ? ` · ${t("Ld.FinalNotPlaced")}` : ""}`;

// Edit shot overlay: prompt + keyframe regenerate. The draft (prompt, picked references) outlives the panel.
function EditShotPanel({
  projectId,
  shot,
  n,
  initialPrompt,
  renderImages,
  regen,
  busy,
  onSave,
  onClose,
  error,
  onDismissError,
}) {
  const [d, patch, clear, discard, dirty] = useDraft(
    projectId,
    `edit:${shot.id}`,
    { prompt: initialPrompt, regenRefs: [] },
    initialPrompt !== shot.prompt
  );
  return (
    <OverlayPanel
      title={`${t("Shot", { n })} · ${t("EditShot")}`}
      helper={t("EditHelper")}
      onClose={onClose}
      onDiscard={dirty ? discard : undefined}
      error={error}
      onDismissError={onDismissError}
      actions={
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {regen && (
            <>
              {regen.modelPick}
              <button
                type="button"
                className="add-video-modal__btn add-video-modal__btn--secondary"
                style={{ fontSize: 12, padding: "4px 12px", display: "flex", gap: 4 }}
                disabled={regen.disabled}
                onClick={() => {
                  clear();
                  regen.run(d.regenRefs);
                }}
              >
                <LuRefreshCw size={12} />
                {regen.label}
              </button>
            </>
          )}
          <button
            type="button"
            className="add-video-modal__btn add-video-modal__btn--primary"
            style={{ fontSize: 12, padding: "6px 14px", borderRadius: "4px" }}
            disabled={busy || !d.prompt.trim()}
            onClick={() => {
              clear();
              onSave(d.prompt.trim());
            }}
          >
            {tv("Save")}
          </button>
          <button
            type="button"
            className="add-video-modal__btn add-video-modal__btn--ghost"
            style={{ fontSize: 12, padding: "6px 14px", borderRadius: "4px" }}
            onClick={onClose}
          >
            {tv("Cancel")}
          </button>
        </div>
      }
    >
      <div className="add-video-modal__vd-cols">
        <div className="add-video-modal__vd-col">
          {renderImages(d.regenRefs, (next) =>
            patch((v) => ({ regenRefs: typeof next === "function" ? next(v.regenRefs) : next }))
          )}
        </div>
        <div className="add-video-modal__vd-col">
          <div className="add-video-modal__scene-field-label" style={frameLabelStyle}>
            {t("Shot", { n })} · {t("Fields.prompt")}
          </div>
          <textarea
            className="add-video-modal__scene-prompt-textarea"
            style={inputStyle}
            rows={9}
            aria-label={t("Fields.prompt")}
            value={d.prompt}
            onChange={(e) => patch({ prompt: e.target.value })}
          />
        </div>
      </div>
    </OverlayPanel>
  );
}

export default function DirectorEditStep({
  project,
  busy,
  canUndo,
  canRedo,
  onOp: rawOp,
  onUndo,
  onRedo,
  onRegenerate,
  initialVideoModel = "veo-fast", // the storyboard step's pick
  simple = false, // animate-profile project: no add-shot / regenerate / keyframe / edit-shot UI
  onContinue,
  onSaveShot,
  onClipAudio,
  audioUrl,
  models,
  onRegenerateKeyframe,
  onRefFrame,
  onRefUpload,
  onRefGenerate,
  onRefDelete,
  onRefKeyframe,
  onRemoveRefKeyframe,
  onRefUrl,
  onImportVideo,
  onNewFromScene,
  autoAddShot,
  onAutoAddShotDone,
  onAddShot,
  onApproveShot,
  onFinalize, // L&D: finalize one draft shot (only passed for that flow)
  onNewLd, // L&D: start an L&D video from the empty state
  onFrameDirect, // Frame Agent: (sub, body) => Promise<response | null>
  onNewPrompt, // "Create with your own prompt" from the empty state
  hasNarration, // the scene has narration: enables the two from-scene start paths
  storyOpen,
  onStoryClose,
  cookbook,
  error,
  onDismissError,
}) {
  const { edl, shots } = project;
  // No ready take yet (e.g. an import still being processed): nothing to edit or play.
  const noReady = !shots.some((s) => s.takes.some((tk) => tk.status === "ready" && tk.url));
  // Takes are in flight or failed and none is playable: a focused progress view replaces the editor.
  const showProgress = noReady && shots.some((s) => s.takes.length > 0);
  const [selectedId, setSelectedId] = useState(null);
  const [range, setRange] = useState(null); // { start, end } frames; end null = follows the playhead (Mark range start)
  const [drag, setDrag] = useState(null); // { id, start, end } while TrackClip drags or resizes
  const [nextOpen, setNextOpen] = useState(false);
  const [width, setWidth] = useState(0); // .it-tracks-area client width
  const [zoom, setZoom] = useState(1); // × fit
  const [editing, setEditing] = useState(null); // { shotId, prompt } — inline prompt panel
  const [cutMenu, setCutMenu] = useState(null); // { x, y, segId } — transition chip menu
  const [previewMenu, setPreviewMenu] = useState(null); // { x, y, kind: "preview" | "range" }
  const [sound, setSound] = useState(false); // play the scene narration in sync with the cut
  const [clip, setClip] = useState(project.clipAudio); // clip-sound level; local so the slider is instant, saved debounced
  const [clipMenu, setClipMenu] = useState(null); // { x, y } — clip sound popover
  const clipTimer = useRef(null);
  const clipPending = useRef(null);
  const clipSave = useRef(onClipAudio);
  clipSave.current = onClipAudio;
  const projectIdRef = useRef(project.id);
  projectIdRef.current = project.id;
  const flushClip = () => {
    clearTimeout(clipTimer.current);
    // A write scheduled for another project must not land on the current one.
    if (clipPending.current && clipPending.current.id === projectIdRef.current)
      clipSave.current?.(clipPending.current.next);
    clipPending.current = null;
  };
  const setClipAudio = (next) => {
    setClip(next);
    clipPending.current = { id: project.id, next };
    clearTimeout(clipTimer.current);
    clipTimer.current = setTimeout(flushClip, 400);
  };
  useEffect(() => flushClip, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Re-sync from the project (undo/redo/refetch) unless a local edit is still pending.
  useEffect(() => {
    if (!clipPending.current) setClip(project.clipAudio);
  }, [project.clipAudio]);
  const [finalFrom, setFinalFrom] = useState(null); // { ref } | { takeId, frame } — final-frame panel
  const [frameDirect, setFrameDirect] = useState(null); // { segmentId, frame, url, seekSec, time, range? } — Frame Agent panel
  const [newOpen, setNewOpen] = useState(false); // "N new shots waiting" popover
  const [addShot, setAddShot] = useState(null); // { prefill? } — Add shot panel
  const empty = shots.length === 0; // blank project: no timeline until the first shot exists
  const [emptyPick, setEmptyPick] = useState(null); // "video" | "image" — the empty state's inline library picker
  // A blank project opened from a library image / scene image: Add shot opens with that ref as the start frame.
  useEffect(() => {
    if (!autoAddShot) return;
    setAddShot({ prefill: autoAddShot });
    onAutoAddShotDone?.();
  }, [autoAddShot]); // eslint-disable-line react-hooks/exhaustive-deps
  const [videoModel, setVideoModel] = useState(initialVideoModel);
  const [imageModel, setImageModel] = useState("gemini-nano");
  const [uploading, setUploading] = useState(false);
  const [dismissedGhosts, setDismissedGhosts] = useState([]); // failed-take ids whose placeholder was closed
  // Any edit but remove-range (which clears it itself) drops the stale selection.
  const onOp = (fn) => {
    setRange(null);
    rawOp(fn);
  };
  const [confirmDel, setConfirmDel] = useState(null); // inline confirm: { kind: "ref", id, where } | { kind: "kf" }
  const shotIndex = useMemo(() => new Map(shots.map((s, i) => [s.id, i])), [shots]);
  const shotOf = (seg) => shots[shotIndex.get(seg.shotId)];
  const takeOf = (seg) => shotOf(seg)?.takes.find((tk) => tk.id === seg.takeId);

  const player = useCutPlayer({ edl, takeOf, audioUrl, sound, clipAudio: clip });
  const hasClipSound = edl.some((seg) => takeOf(seg)?.audioTrack);
  const muteRef = useRef(null);
  useEffect(() => {
    if (!hasClipSound) setClipMenu(null);
  }, [hasClipSound]);
  useEffect(() => {
    if (!clipMenu) return undefined;
    muteRef.current?.focus();
    const onKey = (e) => e.key === "Escape" && setClipMenu(null);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [!!clipMenu]); // eslint-disable-line react-hooks/exhaustive-deps
  const clipPct = Math.round(clipLevel(clip) * 100);
  const { total, playhead, setPlayhead, playing, hit, hitTake, takeFrame, stop, togglePlay } =
    player;
  const inspectorOpen =
    nextOpen || !!finalFrom || !!editing || !!addShot || !!storyOpen || !!frameDirect;
  const areaRef = useRef(null);
  const tracksRef = useRef(null);
  const wasSel = useRef(null); // the clip that was selected when this pointer went down
  const picked = useRef(null); // the clip TrackClip just reported as selected
  const moved = useRef(false); // the pointer dragged / resized instead of clicking
  // Fit: the whole EDL spans the tracks area on load, on resize and whenever its length changes.
  useLayoutEffect(() => {
    const el = areaRef.current;
    if (!el) return undefined;
    const read = () => setWidth(el.clientWidth);
    read();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(read);
    observer.observe(el);
    return () => observer.disconnect();
  }, [showProgress, empty]);
  useEffect(() => setZoom(1), [total]);
  const spanSec = Math.max(total, FPS) / FPS;
  const fitPps = width > 0 ? Math.min(240, Math.max(1, (width - PAD - 2) / spanSec)) : DEFAULT_PPS;
  const pps = fitPps * zoom;
  const toX = (frame) => (frame / FPS) * pps;
  // The playhead may rest ON the end (frame === total) so "continue" can start after the last frame.
  const clampFrame = (f) => Math.max(0, Math.min(Math.round(f), total));

  const selected = edl.find((s) => s.id === selectedId) || null;
  const selectedShot = selected ? shotOf(selected) : null;
  const anchor = playhead > 0 ? locate(edl, playhead - 1) : null;
  const anchorShot = anchor ? shotOf(anchor.segment) : null;
  const canInsert =
    !noReady && !busy && !!anchorShot && !edlFull(edl) && !anchorShot.takes.some(isLivePending);
  const vertical = project.brief?.aspect === "9:16";
  // Server continue.next_required: a source shot without a prompt needs "what happens next".
  const nextRequired = !!anchorShot && !anchorShot.prompt?.trim();
  // Insert panel frames. Start = the last frame before the cut; "next" = the first frame after it.
  const anchorTake = anchor ? takeOf(anchor.segment) : null;
  const startFrame = anchor ? anchor.segment.inFrame + anchor.localFrame : 0;
  let after = null;
  if (anchor) {
    const sg = anchor.segment;
    const nx = edl[anchor.index + 1];
    if (anchor.localFrame + 1 < sg.outFrame - sg.inFrame)
      after = { seg: sg, frame: startFrame + 1 };
    else if (nx) after = { seg: nx, frame: nx.inFrame };
  }
  const afterTake = after ? takeOf(after.seg) : null;
  // Frame actions and the reference set need a ready clip under (or just before) the playhead.
  const frameClip = hitTake?.status === "ready" || anchorTake?.status === "ready";
  const pickable = shots.flatMap((s, i) =>
    s.takes
      .map((tk, n) => ({ tk, label: `${t("Shot", { n: i + 1 })} · ${takeName(tk, n)}` }))
      .filter(({ tk }) => tk.status === "ready" && tk.url)
  );
  const hitShot = hit ? shotOf(hit.segment) : null;
  // Add shot placement: after the last segment that ends at/before the playhead (null = the start), or the last one.
  let playSegId = null;
  let upTo = 0;
  for (const sg of edl) {
    upTo += sg.outFrame - sg.inFrame;
    if (upTo > playhead) break;
    playSegId = sg.id;
  }
  const places = {
    playhead: playSegId,
    end: edl.length ? edl[edl.length - 1].id : null,
    // A clip follows each insertion point (bridge into next clip needs one).
    next: {
      playhead: edl.findIndex((sg) => sg.id === playSegId) + 1 < edl.length,
      start: edl.length > 0,
      end: false,
    },
  };
  const newShots = shots.map((s, i) => ({ s, n: i + 1 })).filter(({ s }) => s.takes.length === 0);
  // Visual only: shots still being generated (or just failed) that are not in the EDL yet, anchored
  // after their continue segment (else at the end). Never touches the EDL or the frame math.
  const pendingPlacements = shots.flatMap((s) => {
    if (edl.some((sg) => sg.shotId === s.id) || s.takes.some((tk) => tk.status === "ready"))
      return [];
    const tk = s.takes[s.takes.length - 1];
    if (!tk) return [];
    const failed = tk.status === "failed" || isStale(tk);
    if (!failed && tk.status !== "pending") return [];
    if (failed && dismissedGhosts.includes(tk.id)) return [];
    const at = edl.findIndex((sg) => sg.id === tk.afterSegmentId);
    const frame = at < 0 ? total : segmentStart(edl, at) + edl[at].outFrame - edl[at].inFrame;
    return [{ shotId: s.id, takeId: tk.id, frame, failed }];
  });
  // The Shot details column exists only while a clip/shot is selected; unplaced shots live in a toolbar chip.
  const showSide = !showProgress && !!selected && !!selectedShot;

  const refs = project.refs || [];
  const allTakes = shots.flatMap((s) => s.takes);
  const vModel = effectiveModel(models, "video", videoModel);
  const iModel = effectiveModel(models, "image", imageModel);
  const canAddFrame =
    !busy &&
    hitTake?.status === "ready" &&
    !!hitTake.id &&
    takeFrame !== null &&
    refs.length < MAX_REFS;
  // Ruler seek + playhead drag — IdeaTimeline's gestures, minus its edge auto-scroll.
  // select: a drag of 4+ px also selects the swept range [pointer-down frame, pointer frame].
  const followPointer = (e, toFrame, select, clearOnClick) => {
    const el = e.currentTarget;
    if (playing) stop();
    el.setPointerCapture?.(e.pointerId);
    const x0 = e.clientX;
    const f0 = clampFrame(toFrame(x0, el));
    setPlayhead(f0);
    let dragged = false;
    const onMove = (me) => {
      const f = clampFrame(toFrame(me.clientX, el));
      setPlayhead(f);
      if (select && Math.abs(me.clientX - x0) >= 4) {
        dragged = true;
        setRange({ start: f0, end: f });
      }
    };
    const onUp = () => {
      if (clearOnClick && !dragged) setRange(null);
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
      el.removeEventListener("pointercancel", onUp);
    };
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", onUp);
    el.addEventListener("pointercancel", onUp);
  };
  const xToFrame = (x, el) => ((x - el.getBoundingClientRect().left) / pps) * FPS;
  const seekFromRuler = (e) => followPointer(e, xToFrame, true);
  // Empty track area (not a clip, the playhead or the selection): click seeks, drag selects.
  const seekFromTracks = (e) => {
    if (e.target.closest(".it-clip, .it-playhead-line, .add-video-modal__director-sel")) return;
    setSelectedId(null);
    followPointer(e, xToFrame, true, true);
  };
  // A selection handle moves one edge of the range.
  const dragEdge = (e, edge) => {
    e.stopPropagation();
    const el = e.currentTarget;
    const tracks = tracksRef.current;
    el.setPointerCapture?.(e.pointerId);
    const onMove = (me) => {
      const f = clampFrame(xToFrame(me.clientX, tracks));
      setRange((r) => r && { ...r, [edge]: f });
    };
    const onUp = () => {
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
      el.removeEventListener("pointercancel", onUp);
    };
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", onUp);
    el.addEventListener("pointercancel", onUp);
  };
  const dragPlayhead = (e) => {
    e.stopPropagation();
    const startX = e.clientX;
    const startFrame = playhead;
    followPointer(e, (x) => startFrame + ((x - startX) / pps) * FPS);
  };

  const ghostW = Math.max(110, toX(TAKE_SEC * FPS));
  const totalWidth = Math.max(
    toX(Math.max(total, FPS)) + PAD,
    ...pendingPlacements.map((g) => toX(g.frame) + ghostW + PAD)
  );
  const selLo = range ? Math.min(range.start, range.end ?? playhead) : 0;
  const selHi = range ? Math.max(range.start, range.end ?? playhead) : 0;
  const selStyle = { left: toX(selLo), width: toX(selHi) - toX(selLo) };
  const rulerTicks = useMemo(() => {
    const major = MAJOR_STEPS.find((n) => n * pps >= 60) ?? 60;
    const ticks = [];
    for (let k = 0; (k * major) / 5 <= spanSec + 0.2; k += 1) {
      const sec = (k * major) / 5;
      const isMajor = k % 5 === 0;
      // a label that would overflow the track width (clipped "00:13.") is dropped
      const fits = sec * pps + RULER_LABEL_W <= totalWidth;
      ticks.push({ x: sec * pps, isMajor, label: isMajor && fits ? formatTime(sec * FPS) : null });
    }
    return ticks;
  }, [spanSec, pps, totalWidth]);

  // TrackClip reports: right resize → extra.resizeEdge "right"; left resize →
  // same end, new start (video never touches trimStart); body drag → both moved.
  const handleUpdateEnd = (id, startFrame, endFrame, extra) => {
    setDrag(null);
    const index = edl.findIndex((s) => s.id === id);
    if (index < 0) return;
    startFrame = Math.round(startFrame);
    endFrame = Math.round(endFrame);
    const seg = edl[index];
    const take = takeOf(seg);
    if (!take) return;
    const segStart = segmentStart(edl, index);
    if (startFrame === segStart && endFrame === segStart + seg.outFrame - seg.inFrame) return;
    const findIn = (cur) => cur.find((s) => s.id === id);
    if (extra?.resizeEdge === "right") {
      const length = endFrame - startFrame;
      onOp((cur) => {
        const s = findIn(cur);
        return s ? trimSegment(cur, id, { outFrame: s.inFrame + length }, take.frames) : cur;
      });
    } else if (endFrame === segStart + seg.outFrame - seg.inFrame) {
      const delta = startFrame - segStart;
      onOp((cur) => {
        const s = findIn(cur);
        return s ? trimSegment(cur, id, { inFrame: s.inFrame + delta }, take.frames) : cur;
      });
    } else {
      onOp((cur) => moveSegment(cur, id, dropIndex(cur, id, startFrame)));
    }
  };

  const at = playhead;
  const menuLabels = {
    split: t("SplitHere"),
    "trim-head": t("CutStart"),
    delete: t("DeleteClip"),
    cancel: t("Close"),
  };
  const startEdit = (shotId) =>
    setEditing({ shotId, prompt: shots.find((x) => x.id === shotId)?.prompt ?? "" });
  const canRegen = (shot) => !!shot && !busy && usedTakes(shot) < MAX_TAKES && canGenerate(shot);
  const doSplit = () => onOp((cur) => splitAt(cur, at, newSegId()));
  const doCutAfter = () => onOp((cur) => cutAfter(cur, at));
  const rangeEnd = range ? (range.end ?? playhead) : null;
  const doRemoveRange = () => {
    const from = range.start;
    const to = rangeEnd;
    setRange(null);
    onOp((cur) => removeRange(cur, from, to, newSegId()));
  };
  const addCurrentFrame = () => onRefFrame(hitTake.id, takeFrame);
  const stepFrames = (d) => {
    if (playing) stop();
    setPlayhead(clampFrame(playhead + d));
  };
  // Shortcuts live on the Edit step's root: they fire only while focus is inside it, never while typing.
  const onKeyDown = (e) => {
    const tag = e.target.tagName;
    if (e.key === "Escape" && newOpen && !inspectorOpen) {
      setNewOpen(false);
      e.stopPropagation();
      return;
    }
    if (e.key === "Escape") {
      const menuOpen = !!previewMenu || !!e.target.closest?.("[role=menu]");
      setPreviewMenu(null);
      if (inspectorOpen) {
        setNextOpen(false);
        setFinalFrom(null);
        setEditing(null);
        setAddShot(null);
        setFrameDirect(null);
        onStoryClose?.();
        e.stopPropagation(); // the panel closes first, not the whole Director
      } else {
        setRange(null);
        if (!menuOpen) setSelectedId(null);
      }
      return;
    }
    if (e.target.closest?.(".add-video-modal__vd-overlay")) return; // panel fields keep their own keys
    if (e.target.closest?.("[role=menu]") || e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key === " ") {
      if (/^(INPUT|TEXTAREA|SELECT|BUTTON)$/.test(tag)) return;
      e.preventDefault();
      togglePlay();
      return;
    }
    if (/^(INPUT|TEXTAREA|SELECT)$/.test(tag) || e.target.isContentEditable || noReady) return;
    const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
    const jump = { ArrowLeft: -1, ArrowRight: 1 }[key];
    if (jump) stepFrames(jump * (e.shiftKey ? FPS : 1));
    else if (key === "Home") stepFrames(-playhead);
    else if (key === "End") stepFrames(total - playhead);
    else if (key === "i") setRange((r) => ({ start: playhead, end: r?.end ?? null }));
    else if (key === "o") setRange((r) => ({ start: r ? r.start : 0, end: playhead }));
    else if ((key === "Delete" || key === "Backspace") && range && !rangeNone) doRemoveRange();
    else return;
    e.preventDefault();
  };
  const cutNone = noReady || playhead === 0 || playhead >= total;
  const rangeNone = noReady || !range || rangeEnd === range.start;
  let cursor = 0;
  const clips = edl.map((seg) => {
    const length = seg.outFrame - seg.inFrame;
    const live = drag?.id === seg.id ? drag : null;
    const start = live ? live.start : cursor;
    const end = live ? live.end : cursor + length;
    cursor += length;
    const shot = shotOf(seg);
    const source = takeOf(seg)?.source;
    const draft = project.flow === "ld" && !!takeOf(seg)?.draft;
    const generated = source === "continue";
    const layer = {
      id: seg.id,
      type: "video",
      name: `${seg.zoom ? (seg.zoom === "in" ? "⊕ " : "⊖ ") : ""}${draft ? `${t("Ld.Draft")} · ` : ""}${generated ? `${t("Generated")} · ` : ""}${t(
        "Shot",
        {
          n: (shotIndex.get(seg.shotId) ?? 0) + 1,
        }
      )} · ${takeName(takeOf(seg) || {}, shot?.takes.findIndex((tk) => tk.id === seg.takeId) ?? -1)}`,
      startFrame: start,
      endFrame: end,
      trimStart: seg.inFrame,
      mediaUrl: takeOf(seg)?.url,
    };
    return (
      <TrackClip
        key={seg.id}
        layer={layer}
        clipLeft={toX(start)}
        clipWidth={toX(end - start)}
        isSelected={selected?.id === seg.id}
        color={generated ? GENERATED_COLOR : CLIP_COLOR}
        pps={pps}
        fps={FPS}
        currentFrame={at}
        menuSelects={false}
        onSelect={(id) => {
          picked.current = id;
          setSelectedId(id);
        }}
        onUpdate={(id, a, b) => {
          moved.current = true;
          setDrag({ id, start: a, end: b });
        }}
        onUpdateEnd={handleUpdateEnd}
        onSplit={doSplit}
        onTrimHead={(id) =>
          onOp((cur) => {
            const i = cur.findIndex((s) => s.id === id);
            return i < 0 ? cur : removeRange(cur, segmentStart(cur, i), at, newSegId());
          })
        }
        onDelete={(id) => onOp((cur) => deleteSegment(cur, id))}
        menuLabels={menuLabels}
        extraItems={[
          ...(draft && onFinalize
            ? [
                {
                  key: "finalize",
                  icon: <LuSparkles />,
                  label: t("Ld.FinalizeShot"),
                  disabled: busy || !shot,
                  onClick: () => onFinalize(shot.id),
                },
              ]
            : []),
          ...[
            ["none", null, LuMinus],
            ["in", "in", LuZoomIn],
            ["out", "out", LuZoomOut],
          ].map(([key, value, Icon]) => ({
            key: `zoom-${key}`,
            icon: <Icon />,
            label: `${(seg.zoom ?? null) === value ? "✓ " : ""}${t(`Zoom_${key}`)}`,
            onClick: () => onOp((cur) => setEffect(cur, seg.id, "zoom", value)),
          })),
          ...(simple
            ? []
            : [
                {
                  key: "edit-shot",
                  icon: <LuPenLine />,
                  label: t("EditShot"),
                  disabled: busy || !shot || noReady,
                  onClick: () => startEdit(shot.id),
                },
                ...(shot && !isImportShot(shot)
                  ? [
                      {
                        key: "regenerate",
                        icon: <LuRefreshCw />,
                        label: t("Regenerate"),
                        disabled: noReady || !canRegen(shot),
                        onClick: () => onRegenerate(shot.id, vModel),
                      },
                    ]
                  : []),
              ]),
        ]}
      />
    );
  });

  // hint: the keyboard shortcut shown in the tooltip.
  const toolBtn = (Icon, label, onClick, disabled, active, extra = "", hint = "") => (
    <button
      type="button"
      className={`it-btn it-btn--icon it-btn--labeled${extra ? ` ${extra}` : ""}${active ? " it-btn--active" : ""}`}
      title={hint ? `${label} (${hint})` : label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
    >
      <Icon size={16} />
      <span className="it-btn__label">{label}</span>
    </button>
  );

  const videoPick = (
    <ModelPick kind="video" value={videoModel} onChange={setVideoModel} models={models} />
  );

  const editBtn = (shotId) => (
    <button
      type="button"
      className="add-video-modal__btn add-video-modal__btn--ghost"
      style={{ fontSize: 12, padding: "4px 12px", display: "flex", alignItems: "center", gap: 4 }}
      disabled={busy}
      onClick={() => startEdit(shotId)}
    >
      <LuPenLine size={12} />
      {tv("Edit")}
    </button>
  );

  // Shots whose takes have not produced anything usable yet: generating, or failed.
  const waiting = shots
    .map((s, i) => ({ s, i }))
    .filter(({ s }) => s.takes.length > 0 && !s.takes.some((tk) => tk.status === "ready"));
  const waitStatus = (s) => {
    const last = s.takes[s.takes.length - 1];
    return isStale(last) ? "failed" : last.status;
  };
  const generatingCount = waiting.filter(({ s }) => waitStatus(s) === "pending").length;
  // Retry / Edit / safer prompt for a shot whose latest take failed.
  const failedActions = (s) => {
    const last = s.takes[s.takes.length - 1];
    return (
      <>
        {editBtn(s.id)}
        {!isImportShot(s) && (
          <button
            type="button"
            className="add-video-modal__btn add-video-modal__btn--secondary"
            style={{ fontSize: 12, padding: "4px 12px" }}
            disabled={busy || usedTakes(s) >= MAX_TAKES || !canGenerate(s)}
            onClick={() => onRegenerate(s.id, vModel)}
          >
            {priced(t("Retry"), models, "video", videoModel)}
          </button>
        )}
        {last.error === "content_filtered" && last.suggestedPrompt && (
          <button
            type="button"
            className="add-video-modal__btn add-video-modal__btn--secondary"
            style={{ fontSize: 12, padding: "4px 12px" }}
            disabled={busy}
            onClick={() => setEditing({ shotId: s.id, prompt: last.suggestedPrompt })}
          >
            {t("UseSaferPrompt")}
          </button>
        )}
      </>
    );
  };
  // Progress view: one card per shot that has takes, nothing else.
  const renderProgress = () => {
    const cards = shots.map((s, i) => ({ s, i })).filter(({ s }) => s.takes.length > 0);
    const ready = cards.filter(({ s }) => s.takes.some((tk) => tk.status === "ready")).length;
    const pending = cards.filter(({ s }) => waitStatus(s) === "pending").length;
    const minutes = Math.max(1, Math.ceil((pending * 90) / 60)); // ~60-120 s per pending clip
    const anyRetry = cards.some(({ s }) => waitStatus(s) === "failed" && !isImportShot(s));
    return (
      <div className="add-video-modal__director-main">
        <div role="status" aria-label={t("ClipsReady", { ready, total: cards.length })}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 8,
              fontSize: 13,
              color: "#334155",
              marginBottom: 6,
            }}
          >
            <span>{t("ClipsReady", { ready, total: cards.length })}</span>
            {pending > 0 && <span>{t("EstimateMin", { min: minutes })}</span>}
          </div>
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={cards.length}
            aria-valuenow={ready}
            style={{ height: 8, borderRadius: 4, background: "#e2e8f0", overflow: "hidden" }}
          >
            <div
              style={{
                height: "100%",
                width: `${cards.length ? (ready / cards.length) * 100 : 0}%`,
                background: "#17a2b8",
                transition: "width 0.3s",
              }}
            />
          </div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 8 }}>{t("GenerateNote")}</div>
        </div>
        {anyRetry && <div style={{ alignSelf: "flex-start" }}>{videoPick}</div>}
        {error && !inspectorOpen && <ErrorAlert text={error} onClose={onDismissError} />}
        <div className="add-video-modal__vd-grid add-video-modal__vd-grid--board">
          {cards.map(({ s, i }) => {
            const status = s.takes.some((tk) => tk.status === "ready") ? "ready" : waitStatus(s);
            const last = s.takes[s.takes.length - 1];
            const [bg, color] = {
              pending: ["#ecfeff", "#17a2b8"],
              failed: ["#fef2f2", "#ef4444"],
              ready: ["#f0fdf4", "#16a34a"],
            }[status];
            return (
              <div
                key={s.id}
                className="add-video-modal__scene-card add-video-modal__vd-card"
                style={{ borderColor: color }}
              >
                <div className="add-video-modal__vd-img">
                  {s.keyframe?.url && (
                    <img src={s.keyframe.url} alt="" style={{ objectFit: "contain" }} />
                  )}
                </div>
                <div className="add-video-modal__scene-title">
                  <span className="add-video-modal__scene-num">{t("Shot", { n: i + 1 })}</span>
                  <span className="add-video-modal__scene-time">{s.durationSec}s</span>
                  <span className="add-video-modal__scene-badge" style={{ background: bg, color }}>
                    {status === "pending" && <span className="add-video-modal__scene-spinner" />}
                    {status === "pending"
                      ? tv("StatusGenerating")
                      : status === "failed"
                        ? tv("StatusFailed")
                        : t("ChipReady")}
                  </span>
                </div>
                {status === "failed" && (
                  <>
                    <div style={{ fontSize: "13px", color: "#991b1b" }}>{takeError(last)}</div>
                    <PolicyChip item={last} />
                    <div className="add-video-modal__vd-actions">{failedActions(s)}</div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const lastTake = selectedShot?.takes[selectedShot.takes.length - 1];
  const retry =
    !!lastTake &&
    !selectedShot.takes.some((tk) => tk.status === "ready") &&
    (lastTake.status === "failed" || isStale(lastTake));

  const rangeActions = [
    {
      key: "remove",
      Icon: LuScissorsLineDashed,
      label: t("RemoveRange"),
      onClick: doRemoveRange,
      disabled: rangeNone,
    },
  ];

  // Per-reference menu: keyframe of the selected shot, final frame, delete.
  const refActions = (r) => {
    const ready = r.status === "ready" && !!r.url;
    const parent = r.parentRefId && refs.find((x) => x.id === r.parentRefId);
    const saferFrom = parent
      ? { ref: parent }
      : r.fromTakeId && Number.isFinite(r.frame) && { takeId: r.fromTakeId, frame: r.frame };
    return [
      {
        key: "safer",
        Icon: LuSparkles,
        label: t("UseSaferPrompt"),
        hidden: !(r.status === "failed" && r.error === "content_filtered" && r.suggestedPrompt),
        disabled: busy || !saferFrom,
        onClick: () => setFinalFrom({ ...saferFrom, prompt: r.suggestedPrompt }),
      },
      {
        key: "keyframe",
        Icon: LuImage,
        label: t("UseAsKeyframe"),
        disabled: !ready || busy || !selectedShot || isImportShot(selectedShot),
        onClick: () => onRefKeyframe(selectedShot.id, r.id),
      },
      {
        key: "final",
        Icon: LuSparkles,
        label: t("GenerateFinal"),
        disabled: !ready,
        onClick: () => setFinalFrom({ ref: r }),
      },
      {
        key: "delete",
        Icon: LuTrash2,
        label: t("DeleteRef"),
        disabled: busy,
        onClick: () => setConfirmDel({ kind: "ref", id: r.id, where: "strip" }),
      },
    ].filter((a) => !simple || a.key === "delete");
  };

  const deleteRef = (id) => {
    setConfirmDel(null);
    onRefDelete(id);
  };
  // Inline (never a popup): Delete / Cancel inside the panel.
  const confirmBar = (text, onYes) => (
    <div
      role="alertdialog"
      aria-label={text}
      style={{
        display: "flex",
        gap: 8,
        alignItems: "center",
        flexWrap: "wrap",
        fontSize: 12,
        marginTop: 6,
      }}
    >
      <span>{text}</span>
      <button
        type="button"
        className="add-video-modal__btn add-video-modal__btn--secondary"
        style={{ fontSize: 12, padding: "2px 10px" }}
        disabled={busy}
        onClick={onYes}
      >
        {t("DeleteRef")}
      </button>
      <button
        type="button"
        className="add-video-modal__btn add-video-modal__btn--secondary"
        style={{ fontSize: 12, padding: "2px 10px" }}
        onClick={() => setConfirmDel(null)}
      >
        {t("RefCancel")}
      </button>
    </div>
  );

  const uploadRef = async (file) => {
    setUploading(true);
    try {
      return await onRefUpload(file);
    } finally {
      setUploading(false);
    }
  };

  // The preview right-click menu lists all; the right bar (bar) keeps only the frame actions.
  const previewActions = [
    {
      key: "split",
      Icon: LuScissors,
      label: t("SplitHere"),
      onClick: doSplit,
      disabled: cutNone,
    },
    {
      key: "insert",
      Icon: LuWandSparkles,
      label: t("InsertShotHere"),
      onClick: () => setNextOpen(true),
      disabled: !canInsert,
      bar: true,
    },
    {
      key: "direct",
      Icon: LuWand,
      label: tf("Direct"),
      onClick: () =>
        setFrameDirect({
          segmentId: anchor.segment.id,
          frame: startFrame + 1, // mw takes the CUT frame; startFrame is the last kept one
          url: anchorTake.url,
          seekSec: (startFrame + 0.5) / FPS,
          time: formatTime(playhead),
          ...(range && rangeEnd !== range.start
            ? { range: { startFrame: selLo, endFrame: selHi } }
            : {}),
        }),
      disabled: !canInsert || !anchorTake?.url,
      hidden: !onFrameDirect,
      bar: true,
    },
    {
      key: "addshot",
      Icon: LuPlus,
      label: t("AddShot"),
      onClick: () => setAddShot({}),
      disabled: busy || noReady || edlFull(edl),
      bar: true,
    },
    {
      key: "cut",
      Icon: LuArrowRightToLine,
      label: t("CutAfter"),
      onClick: doCutAfter,
      disabled: cutNone,
    },
    {
      key: "addref",
      Icon: LuImagePlus,
      label: t("AddToRefs"),
      onClick: addCurrentFrame,
      disabled: !canAddFrame,
      bar: true,
    },
    {
      key: "mark",
      Icon: LuFlag,
      label: t("MarkIn"),
      onClick: () => setRange({ start: playhead, end: null }),
      disabled: total === 0 || noReady,
    },
    {
      key: "range",
      Icon: LuScissorsLineDashed,
      label: t("RemoveRange"),
      onClick: doRemoveRange,
      disabled: rangeNone,
      hidden: range === null,
    },
    {
      key: "final",
      Icon: LuSparkles,
      label: t("UseFrameFinal"),
      onClick: () => setFinalFrom({ takeId: hitTake.id, frame: takeFrame }),
      disabled: !canAddFrame,
      bar: true,
    },
    {
      key: "edit",
      Icon: LuPenLine,
      label: t("EditShot"),
      onClick: () => startEdit(hitShot.id),
      disabled: busy || !hitShot || noReady,
    },
    {
      key: "regen",
      Icon: LuRefreshCw,
      label: t("Regenerate"),
      onClick: () => onRegenerate(hitShot.id, vModel),
      disabled: noReady || !canRegen(hitShot),
      hidden: !!hitShot && isImportShot(hitShot),
    },
    {
      key: "finalize",
      Icon: LuSparkles,
      label: t("Ld.FinalizeShot"),
      onClick: () => onFinalize(hitShot.id),
      disabled: busy,
      hidden: !onFinalize || !hitTake?.draft,
    },
  ].filter((a) => !simple || !["addshot", "final", "edit", "regen"].includes(a.key));

  // Edit shot: every image this shot is made from, and refs to send with a keyframe regenerate.
  const es = editing ? shots.find((x) => x.id === editing.shotId) : null;
  let regen = null;
  let renderEditImages = () => null;
  if (es) {
    const cont = [...(es?.takes || [])].reverse().find((tk) => tk.source === "continue");
    // [label, node(preview), lightbox item]: the preview prop is the collection, known once all are listed.
    const frameOf = (label, takeId, frame) => {
      const src = allTakes.find((tk) => tk.id === takeId);
      if (!src?.url || !Number.isInteger(frame)) return null;
      const seekSec = (frame + 0.5) / FPS;
      return [
        label,
        (pv) => (
          <PreviewThumb preview={pv} clickable className="add-video-modal__vd-pv-wrap--fill">
            <VideoThumb
              url={src.url}
              seekSec={seekSec}
              className="add-video-modal__director-frame-thumb"
            />
          </PreviewThumb>
        ),
        { url: src.url, kind: "video", seekSec, name: label },
      ];
    };
    const used = [
      es?.keyframe?.status === "ready" && es.keyframe.url
        ? [
            t("KeyframeImage"),
            (pv) => (
              <React.Fragment key="k">
                <RefImage item={es.keyframe} preview={pv} />
                {es.keyframe.fromRefId && (
                  <>
                    <span style={{ fontSize: 11, color: "#64748b" }}>{t("FromRef")}</span>
                    <button
                      type="button"
                      className="add-video-modal__btn add-video-modal__btn--secondary"
                      style={{ fontSize: 11, padding: "2px 8px" }}
                      disabled={busy}
                      onClick={() => setConfirmDel({ kind: "kf" })}
                    >
                      {t("RemoveRefKeyframe")}
                    </button>
                  </>
                )}
              </React.Fragment>
            ),
            { url: es.keyframe.url, name: t("KeyframeImage"), description: es.keyframe.prompt },
          ]
        : null,
      cont && frameOf(t("StartFrame"), cont.fromTakeId, cont.fromFrame),
      cont?.endFromTakeId && frameOf(t("EndFrame"), cont.endFromTakeId, cont.endFromFrame),
      ...(es?.castIds || []).map((id) => {
        const m = project.bible?.cast.find((c) => c.id === id);
        return m?.still?.url
          ? [
              m.name,
              (pv) => <RefImage key={id} item={m.still} preview={pv} />,
              { url: m.still.url, name: m.name, description: m.description },
            ]
          : null;
      }),
      (() => {
        const m = project.bible?.locations.find((l) => l.id === es?.locationId);
        return m?.still?.url
          ? [
              m.name,
              (pv) => <RefImage key="loc" item={m.still} preview={pv} />,
              { url: m.still.url, name: m.name, description: m.description },
            ]
          : null;
      })(),
    ].filter(Boolean);
    const usedItems = used.map(([, , item]) => item);
    const readyRefs = refs.filter((r) => r.status === "ready" && r.url);
    const regenPreviews = readyRefs.map((r) => ({
      url: r.url,
      name: t("RefN", { n: refs.indexOf(r) + 1 }),
      description: r.prompt,
    }));
    const canKeyframe = !isImportShot(es) && !isContinueShot(es);
    if (canKeyframe) {
      regen = {
        modelPick: (
          <ModelPick kind="image" value={imageModel} onChange={setImageModel} models={models} />
        ),
        label: priced(t("RegenKeyframe"), models, "image", imageModel),
        disabled: busy || isLivePending(es.keyframe),
        run: (ids) => {
          onRegenerateKeyframe(es.id, {
            refIds: ids.filter((id) => readyRefs.some((r) => r.id === id)),
            model: iModel,
          });
          setEditing(null);
        },
      };
    }
    renderEditImages = (regenRefs, setRegenRefs) => (
      <>
        {used.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <div className="add-video-modal__scene-field-label" style={frameLabelStyle}>
              {t("ImagesUsed")}
            </div>
            <div className="add-video-modal__director-refs-list">
              {used.map(([label, node], i) => (
                <figure key={label} className="add-video-modal__director-ref" style={{ margin: 0 }}>
                  {node({ items: usedItems, index: i })}
                  <figcaption style={{ fontSize: 11, color: "#64748b" }}>{label}</figcaption>
                </figure>
              ))}
            </div>
            {confirmDel?.kind === "kf" &&
              confirmBar(t("ConfirmRemoveRefKeyframe"), () => {
                setConfirmDel(null);
                onRemoveRefKeyframe(es.id);
              })}
          </div>
        )}
        {canKeyframe && (
          <div style={{ marginTop: 10 }}>
            <div className="add-video-modal__scene-field-label" style={frameLabelStyle}>
              {t("RegenRefs", { n: regenRefs.length, max: MAX_REGEN_REFS })}
            </div>
            {readyRefs.length === 0 ? (
              <div style={{ fontSize: 12, color: "#64748b" }}>{t("RegenRefsEmpty")}</div>
            ) : (
              <div className="add-video-modal__director-refs-list">
                {readyRefs.map((r) => {
                  const on = regenRefs.includes(r.id);
                  const n = refs.indexOf(r) + 1;
                  const chipBtn = {
                    position: "absolute",
                    top: 2,
                    padding: 2,
                    lineHeight: 0,
                    cursor: "pointer",
                  };
                  return (
                    <div
                      key={r.id}
                      style={{
                        position: "relative",
                        flex: "0 1 112px",
                        minWidth: 72,
                        maxWidth: "100%",
                      }}
                    >
                      <button
                        type="button"
                        aria-pressed={on}
                        aria-label={t("RefN", { n })}
                        disabled={!on && regenRefs.length >= MAX_REGEN_REFS}
                        className={`add-video-modal__director-ref add-video-modal__director-ref--pick${on ? " add-video-modal__director-ref--on" : ""}`}
                        onClick={() =>
                          setRegenRefs(
                            on ? regenRefs.filter((id) => id !== r.id) : [...regenRefs, r.id]
                          )
                        }
                      >
                        <RefImage
                          item={r}
                          preview={{
                            items: regenPreviews,
                            index: readyRefs.indexOf(r),
                            click: false,
                          }}
                        />
                      </button>
                      {on && (
                        <button
                          type="button"
                          aria-label={`${t("DeselectRef")} ${n}`}
                          style={{ ...chipBtn, left: 2 }}
                          onClick={() => setRegenRefs(regenRefs.filter((id) => id !== r.id))}
                        >
                          <LuX size={12} />
                        </button>
                      )}
                      <button
                        type="button"
                        aria-label={`${t("DeleteFromRefs")} ${n}`}
                        title={t("DeleteFromRefs")}
                        style={{ ...chipBtn, right: 2 }}
                        disabled={busy}
                        onClick={() =>
                          setConfirmDel({
                            kind: "ref",
                            id: r.id,
                            where: "edit",
                            unpick: () => setRegenRefs((ids) => ids.filter((x) => x !== r.id)),
                          })
                        }
                      >
                        <LuTrash2 size={12} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            {confirmDel?.kind === "ref" &&
              confirmDel.where === "edit" &&
              confirmBar(t("ConfirmDeleteRef"), () => {
                confirmDel.unpick?.();
                deleteRef(confirmDel.id);
              })}
          </div>
        )}
      </>
    );
  }

  return (
    <div
      className={`add-video-modal__director-edit${showSide ? "" : " add-video-modal__director-edit--solo"}`}
      tabIndex={-1}
      onKeyDown={onKeyDown}
    >
      {showProgress && renderProgress()}
      {!showProgress && empty && (
        <div className="add-video-modal__director-main">
          <div className="add-video-modal__vd-empty" role="region" aria-label={t("EmptyTitle")}>
            <LuClapperboard size={36} color="#94a3b8" aria-hidden="true" />
            <div className="add-video-modal__status-title">{t("EmptyTitle")}</div>
            <div style={{ fontSize: 13, color: "#64748b" }}>{t("EmptyHint")}</div>
            {simple ? null : emptyPick ? (
              <div style={{ width: "100%", maxWidth: 640, height: 280 }}>
                <SlotPicker
                  title={emptyPick === "video" ? t("EmptyImportVideo") : t("EmptyAddImage")}
                  onDone={() => setEmptyPick(null)}
                >
                  <LibraryPicker
                    kind={emptyPick}
                    onPick={async ({ url }) => {
                      const kind = emptyPick;
                      setEmptyPick(null);
                      if (kind === "video") return onImportVideo(url);
                      const refId = await onRefUrl(url);
                      return refId && setAddShot({ prefill: { keyframeRefId: refId } });
                    }}
                  />
                </SlotPicker>
              </div>
            ) : (
              <>
                <StartOptions
                  busy={busy}
                  hasNarration={hasNarration}
                  onCinematic={onNewFromScene}
                  onLd={onNewLd}
                  onPrompt={onNewPrompt}
                />
                <div className="add-video-modal__vd-empty-actions">
                  <button
                    type="button"
                    className="add-video-modal__btn add-video-modal__btn--ghost"
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                    disabled={busy}
                    onClick={() => setAddShot({})}
                  >
                    <LuPlus size={14} />
                    {t("EmptyAddShot")}
                  </button>
                  <button
                    type="button"
                    className="add-video-modal__btn add-video-modal__btn--ghost"
                    title={t("EmptyImportHint")}
                    disabled={busy}
                    onClick={() => setEmptyPick("video")}
                  >
                    {t("EmptyImportVideo")}
                  </button>
                  <button
                    type="button"
                    className="add-video-modal__btn add-video-modal__btn--ghost"
                    disabled={busy || refs.length >= MAX_REFS}
                    onClick={() => setEmptyPick("image")}
                  >
                    {t("EmptyAddImage")}
                  </button>
                </div>
              </>
            )}
            {error && !inspectorOpen && <ErrorAlert text={error} onClose={onDismissError} />}
          </div>
          {refs.length > 0 && (
            <RefStrip
              refs={refs}
              showAddFrame={false}
              uploading={uploading || busy}
              onUpload={onRefUpload ? uploadRef : undefined}
              actionsFor={refActions}
            />
          )}
        </div>
      )}
      {!showProgress && !empty && (
        <>
          <div className="add-video-modal__director-main">
            {waiting.length > 0 && (
              <div role="status" className="add-video-modal__director-hint">
                {generatingCount > 0 && (
                  <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span className="add-video-modal__scene-spinner" />
                    {t("ClipsGenerating", { count: generatingCount })}
                  </span>
                )}
                {waiting
                  .filter(({ s }) => waitStatus(s) === "failed")
                  .map(({ s, i }) => (
                    <span
                      key={s.id}
                      style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}
                    >
                      {t("Shot", { n: i + 1 })} · {tv("StatusFailed")} ·{" "}
                      {takeError(s.takes[s.takes.length - 1])}
                      {failedActions(s)}
                    </span>
                  ))}
              </div>
            )}

            {newShots.length > 0 && (
              <div className="add-video-modal__director-newshots">
                <button
                  type="button"
                  aria-expanded={newOpen}
                  aria-haspopup="dialog"
                  className="add-video-modal__ai-chip add-video-modal__director-model add-video-modal__ai-chip--active"
                  onClick={() => setNewOpen((o) => !o)}
                >
                  <LuWandSparkles size={12} />
                  {t("NewShotsWaiting", { count: newShots.length })}
                </button>
                {newOpen && (
                  <div
                    role="dialog"
                    aria-label={t("NewShots")}
                    className="add-video-modal__director-newshots-pop"
                  >
                    {newShots.map(({ s, n }) => (
                      <NewShotCard
                        key={s.id}
                        shot={s}
                        n={n}
                        edl={edl}
                        refs={refs}
                        models={models}
                        videoModel={videoModel}
                        modelPick={videoPick}
                        aspect={project.brief?.aspect}
                        busy={busy}
                        onApprove={onApproveShot}
                        onGenerate={(shotId, endFrame, refIds) =>
                          refIds?.length
                            ? onRegenerate(shotId, vModel, endFrame, refIds)
                            : onRegenerate(shotId, vModel, endFrame)
                        }
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="add-video-modal__director-stage">
              <div
                onContextMenu={(e) => {
                  e.preventDefault();
                  setPreviewMenu({ x: e.clientX, y: e.clientY, kind: "preview" });
                }}
                className="add-video-modal__director-preview"
                style={{ aspectRatio: vertical ? "9/16" : "16/9" }}
              >
                <CutVideos player={player} sound={sound} audioUrl={audioUrl} />
              </div>
              <div
                className="add-video-modal__director-bar"
                role="group"
                aria-label={t("ShotActions")}
              >
                {previewActions
                  .filter((x) => x.bar && !x.hidden && (x.key === "addshot" || frameClip))
                  .map(({ key, Icon, label, onClick, disabled }) => (
                    <React.Fragment key={key}>
                      {toolBtn(Icon, label, onClick, disabled, false)}
                    </React.Fragment>
                  ))}
              </div>
            </div>
            {error && !inspectorOpen && <ErrorAlert text={error} onClose={onDismissError} />}
            {!noReady && !anchor && (
              <div className="add-video-modal__director-hint">
                <span>{t("InsertNeedsPlayhead")}</span>
                {!simple && (
                  <button
                    type="button"
                    className="add-video-modal__btn add-video-modal__btn--secondary"
                    style={{
                      fontSize: 12,
                      padding: "4px 12px",
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                    disabled={busy || edlFull(edl)}
                    onClick={() => setAddShot({})}
                  >
                    <LuPlus size={12} />
                    {t("AddShotAtStart")}
                  </button>
                )}
              </div>
            )}
            {cutMenu && (
              <ActionMenu
                x={cutMenu.x}
                y={cutMenu.y}
                actions={[
                  ["none", null],
                  ["crossfade", "crossfade"],
                  ["fade", "fade"],
                ].map(([key, value]) => ({
                  key,
                  Icon:
                    (edl.find((s) => s.id === cutMenu.segId)?.transition ?? null) === value
                      ? LuCheck
                      : LuBlend,
                  label: t(`Transition_${key}`),
                  onClick: () => onOp((cur) => setEffect(cur, cutMenu.segId, "transition", value)),
                }))}
                onClose={() => setCutMenu(null)}
              />
            )}
            {clipMenu &&
              createPortal(
                <>
                  <div
                    style={{ position: "fixed", inset: 0, zIndex: MENU_Z }}
                    onPointerDown={() => setClipMenu(null)}
                  />
                  <div
                    role="dialog"
                    aria-label={t("ClipSound")}
                    className="it-clip-context-menu"
                    style={{
                      position: "fixed",
                      left: clipMenu.x,
                      top: clipMenu.y,
                      zIndex: MENU_Z + 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "8px 12px",
                    }}
                  >
                    <label style={{ display: "flex", alignItems: "center", gap: 6, margin: 0 }}>
                      <input
                        ref={muteRef}
                        type="checkbox"
                        checked={!!clip?.muted}
                        onChange={(e) =>
                          setClipAudio({
                            muted: e.target.checked,
                            volume: clipLevel({ volume: clip?.volume }),
                          })
                        }
                      />
                      {t("Mute")}
                    </label>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={clipPct}
                      aria-label={t("ClipSound")}
                      onChange={(e) =>
                        setClipAudio({ muted: !!clip?.muted, volume: Number(e.target.value) / 100 })
                      }
                    />
                    <span style={{ minWidth: 36 }}>{clipPct}%</span>
                  </div>
                </>,
                document.body
              )}
            {previewMenu && (
              <ActionMenu
                x={previewMenu.x}
                y={previewMenu.y}
                actions={previewMenu.kind === "range" ? rangeActions : previewActions}
                onClose={() => setPreviewMenu(null)}
              />
            )}

            <div className="it-timeline add-video-modal__director-timeline">
              <div className="it-toolbar">
                <div className="it-toolbar__left">
                  {toolBtn(
                    playing ? LuPause : LuPlay,
                    playing ? t("Pause") : t("Play"),
                    togglePlay,
                    total === 0 || noReady,
                    false,
                    "it-btn--fixed",
                    "Space"
                  )}
                  <div className="add-video-modal__director-time-pill">
                    {nudgeBtn(
                      `${t("PrevFrame")} (←)`,
                      LuChevronLeft,
                      () => stepFrames(-1),
                      noReady || playhead <= 0
                    )}
                    <div className="it-time-display">
                      <span className="it-time-display__current">{formatTime(playhead)}</span>
                      <span className="it-time-display__sep">/</span>
                      <span className="it-time-display__total">{formatTime(total)}</span>
                    </div>
                    {nudgeBtn(
                      `${t("NextFrame")} (→)`,
                      LuChevronRight,
                      () => stepFrames(1),
                      noReady || playhead >= total
                    )}
                  </div>
                  <button
                    type="button"
                    className={`it-btn it-btn--icon it-btn--labeled${sound ? " it-btn--active" : ""}`}
                    title={audioUrl ? t("PlaySound") : t("NoNarration")}
                    aria-label={t("PlaySound")}
                    aria-pressed={sound}
                    disabled={!audioUrl}
                    onClick={() => setSound((v) => !v)}
                  >
                    {sound ? <LuVolume2 size={16} /> : <LuVolumeX size={16} />}
                    <span className="it-btn__label">{t("PlaySound")}</span>
                  </button>
                  <button
                    type="button"
                    className="it-btn it-btn--icon it-btn--labeled"
                    title={hasClipSound ? t("ClipSound") : t("NoClipSound")}
                    aria-label={t("ClipSound")}
                    aria-haspopup="true"
                    aria-expanded={!!clipMenu}
                    disabled={!hasClipSound}
                    onClick={(e) => {
                      const r = e.currentTarget.getBoundingClientRect();
                      // clamp into the viewport (popover is ~280 x 48)
                      const y =
                        r.bottom + 4 + 48 > window.innerHeight ? r.top - 4 - 48 : r.bottom + 4;
                      setClipMenu({
                        x: Math.max(8, Math.min(r.left, window.innerWidth - 280 - 8)),
                        y: Math.max(8, y),
                      });
                    }}
                  >
                    {clipPct === 0 ? <LuVolumeX size={16} /> : <LuMusic size={16} />}
                    <span className="it-btn__label">{t("ClipSound")}</span>
                  </button>
                  <div className="it-toolbar__divider" />
                  {toolBtn(LuUndo2, i18n.t("Undo"), onUndo, !canUndo)}
                  {toolBtn(LuRedo2, i18n.t("Redo"), onRedo, !canRedo)}
                  <div className="it-toolbar__divider" />
                  {toolBtn(
                    LuFlag,
                    t("MarkIn"),
                    () => setRange({ start: playhead, end: null }),
                    total === 0,
                    range !== null,
                    "",
                    "I / O"
                  )}
                  {toolBtn(
                    LuScissorsLineDashed,
                    t("RemoveRange"),
                    doRemoveRange,
                    rangeNone,
                    false,
                    "",
                    "Delete"
                  )}
                  {toolBtn(LuScissors, t("SplitHere"), doSplit, cutNone)}
                  {toolBtn(LuArrowRightToLine, t("CutAfter"), doCutAfter, cutNone)}
                  <div className="it-toolbar__divider" />
                  {range !== null && (
                    <span className="add-video-modal__director-range">
                      {t("RangeHint", {
                        from: formatTime(Math.min(range.start, rangeEnd)),
                        to: formatTime(Math.max(range.start, rangeEnd)),
                      })}
                    </span>
                  )}
                  <div className="it-zoom-mini" role="group" aria-label="Zoom">
                    <button
                      type="button"
                      className="it-btn--icon it-btn--xs"
                      title={i18n.t("ZoomOut")}
                      aria-label={i18n.t("ZoomOut")}
                      disabled={zoom <= 1}
                      onClick={() => setZoom((z) => Math.max(1, z / ZOOM_STEP))}
                    >
                      <LuMinus size={13} />
                    </button>
                    <button
                      type="button"
                      className="it-zoom-mini__value"
                      title={t("Fit")}
                      aria-label={t("Fit")}
                      onClick={() => setZoom(1)}
                    >
                      {Math.round(zoom * 100)}%
                    </button>
                    <button
                      type="button"
                      className="it-btn--icon it-btn--xs"
                      title={i18n.t("ZoomIn")}
                      aria-label={i18n.t("ZoomIn")}
                      disabled={zoom >= MAX_ZOOM}
                      onClick={() => setZoom((z) => Math.min(MAX_ZOOM, z * ZOOM_STEP))}
                    >
                      <LuPlus size={13} />
                    </button>
                  </div>
                </div>
              </div>
              <div className="it-tracks-area" ref={areaRef}>
                <div className="it-ruler-row">
                  <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
                    <Ruler
                      totalWidth={totalWidth}
                      rulerTicks={rulerTicks}
                      playheadX={toX(playhead)}
                      onPointerDown={seekFromRuler}
                      onPlayheadDrag={dragPlayhead}
                    />
                  </div>
                </div>
                <div
                  className="it-tracks"
                  ref={tracksRef}
                  style={{ minWidth: totalWidth }}
                  onPointerDown={seekFromTracks}
                >
                  {range && rangeEnd !== range.start && (
                    <>
                      <div className="add-video-modal__director-sel-wash" style={selStyle} />
                      <div
                        className="add-video-modal__director-sel"
                        role="group"
                        aria-label={t("Selection")}
                        style={selStyle}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          setPreviewMenu({ x: e.clientX, y: e.clientY, kind: "range" });
                        }}
                      >
                        {["start", "end"].map((edge) => (
                          <span
                            key={edge}
                            role="slider"
                            aria-label={t(edge === "start" ? "SelectionStart" : "SelectionEnd")}
                            aria-valuenow={edge === "start" ? range.start : rangeEnd}
                            className={`add-video-modal__director-sel-handle add-video-modal__director-sel-handle--${
                              (edge === "start") === range.start <= rangeEnd ? "lo" : "hi"
                            }`}
                            onPointerDown={(e) => dragEdge(e, edge)}
                          />
                        ))}
                      </div>
                    </>
                  )}
                  <PlayheadLine x={toX(playhead)} onPointerDown={dragPlayhead} />
                  {pendingPlacements.length > 0 && (
                    <div
                      className="it-track-row"
                      style={{ position: "relative", height: 28, pointerEvents: "none" }}
                    >
                      {pendingPlacements.map((g) => (
                        <div
                          key={g.takeId}
                          data-testid="shot-placeholder"
                          style={{
                            position: "absolute",
                            left: toX(g.frame),
                            width: ghostW,
                            top: 2,
                            height: 24,
                            boxSizing: "border-box",
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "0 6px",
                            fontSize: 11,
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            borderRadius: 4,
                            border: `1px dashed ${g.failed ? "#dc2626" : GENERATED_COLOR}`,
                            borderLeft: `2px solid ${g.failed ? "#dc2626" : GENERATED_COLOR}`,
                            background: "rgba(139,92,246,0.08)",
                            color: g.failed ? "#dc2626" : "#64748b",
                          }}
                        >
                          {!g.failed && <span className="add-video-modal__scene-spinner" />}
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                            {t(g.failed ? "ShotFailed" : "ShotInProgress")}
                          </span>
                          {g.failed && (
                            <>
                              <button
                                type="button"
                                title={t("Retry")}
                                aria-label={t("Retry")}
                                disabled={busy || !canRegen(shots.find((x) => x.id === g.shotId))}
                                style={{
                                  pointerEvents: "auto",
                                  border: 0,
                                  background: "none",
                                  padding: 0,
                                }}
                                onPointerDown={(e) => e.stopPropagation()}
                                onClick={() => onRegenerate(g.shotId, vModel)}
                              >
                                <LuRefreshCw size={12} />
                              </button>
                              <button
                                type="button"
                                aria-label={t("Close")}
                                style={{
                                  pointerEvents: "auto",
                                  border: 0,
                                  background: "none",
                                  padding: 0,
                                }}
                                onPointerDown={(e) => e.stopPropagation()}
                                onClick={() => setDismissedGhosts((d) => [...d, g.takeId])}
                              >
                                <LuX size={12} />
                              </button>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="it-track-row it-track-row--main">
                    <div
                      className="it-track-clips"
                      onPointerDownCapture={(e) => {
                        wasSel.current =
                          e.button === 0 && e.target.closest(".it-clip") ? selected?.id : null;
                        moved.current = false;
                      }}
                      onClick={(e) => {
                        // A plain click on the already-selected clip deselects it.
                        if (
                          wasSel.current &&
                          wasSel.current === picked.current &&
                          !moved.current &&
                          e.target.closest(".it-clip")
                        )
                          setSelectedId(null);
                        wasSel.current = null;
                      }}
                    >
                      {clips}
                      {edl.map((seg, i) => {
                        if (i === 0) return null;
                        const eff = effectiveTransition(edl, i, (x) => takeOf(x)?.frames);
                        const fallback = seg.transition === "crossfade" && eff === "fade";
                        const skipped = !!seg.transition && !eff;
                        const label = skipped
                          ? t("TransitionSkipped")
                          : fallback
                            ? t("TransitionFallback")
                            : t(`Transition_${seg.transition ?? "none"}`);
                        return (
                          <button
                            key={`cut-${seg.id}`}
                            type="button"
                            data-testid="transition-chip"
                            title={label}
                            aria-label={t("TransitionChip")}
                            style={{
                              position: "absolute",
                              left: toX(segmentStart(edl, i)) - 9,
                              top: 2,
                              width: 18,
                              height: 18,
                              zIndex: 5,
                              padding: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: 9,
                              border: "1px solid rgba(255,255,255,.5)",
                              background: seg.transition ? "#7c3aed" : "rgba(0,0,0,.55)",
                              color: fallback ? "#fbbf24" : "#fff",
                              cursor: "pointer",
                              opacity: skipped ? 0.4 : 1,
                            }}
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              const r = e.currentTarget.getBoundingClientRect();
                              setCutMenu({ x: r.left, y: r.bottom, segId: seg.id });
                            }}
                          >
                            {fallback ? <LuTriangleAlert size={11} /> : <LuBlend size={11} />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {!noReady && (
              <RefStrip
                refs={refs}
                showAddFrame={frameClip}
                canAddFrame={canAddFrame}
                uploading={uploading || busy}
                onAddFrame={addCurrentFrame}
                onUpload={onRefUpload ? uploadRef : undefined}
                actionsFor={refActions}
              />
            )}
            {confirmDel?.kind === "ref" &&
              confirmDel.where === "strip" &&
              confirmBar(t("ConfirmDeleteRef"), () => deleteRef(confirmDel.id))}
          </div>

          {showSide && (
            <aside className="add-video-modal__director-side" aria-label={t("ShotDetails")}>
              <div
                className="add-video-modal__vd-label"
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
              >
                {t("ShotDetails")}
                <button
                  type="button"
                  className="add-video-modal__btn add-video-modal__btn--secondary"
                  style={{ padding: 2, lineHeight: 0 }}
                  aria-label={t("Close")}
                  onClick={() => setSelectedId(null)}
                >
                  <LuX size={12} />
                </button>
              </div>
              {selected && selectedShot && (
                <div>
                  <div
                    className="add-video-modal__scene-field-label"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 6,
                      marginBottom: 8,
                    }}
                  >
                    <span style={{ fontSize: "13px", fontWeight: 600, color: "#334155" }}>
                      {t("Shot", { n: shotIndex.get(selectedShot.id) + 1 })}
                    </span>
                    <div
                      style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}
                    >
                      {!simple && editBtn(selectedShot.id)}
                      {!simple && !isImportShot(selectedShot) && videoPick}
                      {!simple && !isImportShot(selectedShot) && (
                        <button
                          type="button"
                          className="add-video-modal__btn add-video-modal__btn--secondary"
                          style={{
                            fontSize: 12,
                            padding: "4px 12px",
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                          disabled={
                            busy ||
                            usedTakes(selectedShot) >= MAX_TAKES ||
                            !canGenerate(selectedShot)
                          }
                          onClick={() => onRegenerate(selectedShot.id, vModel)}
                        >
                          <LuRefreshCw size={12} />
                          {priced(
                            retry ? t("Retry") : t("Regenerate"),
                            models,
                            "video",
                            videoModel
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                  <PolicyChip item={selectedShot.takes.find((tk) => tk.id === selected.takeId)} />
                  <div
                    className="add-video-modal__vd-clamp add-video-modal__vd-clamp--3"
                    style={{ marginBottom: 8 }}
                  >
                    {selectedShot.prompt}
                  </div>
                  <div className="add-video-modal__director-takes">
                    {selectedShot.takes.map((tk, n) => {
                      const readyTakes = selectedShot.takes
                        .map((x, i) => ({ x, i }))
                        .filter(({ x }) => x.status === "ready" && !isStale(x) && x.url);
                      const takeItems = readyTakes.map(({ x, i }) => ({
                        url: x.url,
                        kind: "video",
                        name: takeName(x, i),
                        description: selectedShot.prompt,
                        actions:
                          x.id === selected.takeId
                            ? []
                            : [
                                {
                                  key: "use",
                                  label: t("UseTake"),
                                  icon: LuCheck,
                                  primary: true,
                                  onClick: () =>
                                    onOp((cur) => selectTake(cur, selected.id, x.id, x.frames)),
                                },
                              ],
                      }));
                      const status = isStale(tk) ? "failed" : tk.status;
                      const active = tk.id === selected.takeId;
                      return (
                        <button
                          key={tk.id}
                          type="button"
                          aria-pressed={active}
                          className={`add-video-modal__ai-style-card${active ? " add-video-modal__ai-style-card--active" : ""}`}
                          style={{ position: "relative", overflow: "hidden", minHeight: "100px" }}
                          onClick={() => {
                            if (status === "ready" && !active) {
                              onOp((cur) => selectTake(cur, selected.id, tk.id, tk.frames));
                            }
                          }}
                        >
                          {status === "ready" && (
                            <VideoThumb
                              url={tk.url}
                              seekSec={0.5}
                              className="add-video-modal__director-take-thumb"
                            />
                          )}
                          {status === "ready" && tk.url && (
                            <PreviewButton
                              items={takeItems}
                              index={readyTakes.findIndex(({ x }) => x.id === tk.id)}
                            />
                          )}
                          {status === "pending" && (
                            <span className="add-video-modal__scene-spinner" />
                          )}
                          {status === "failed" && (
                            <span style={{ fontSize: 11, color: "#ef4444" }}>{takeError(tk)}</span>
                          )}
                          <span
                            style={{
                              position: "relative",
                              zIndex: 1,
                              marginTop: status === "ready" ? "78px" : "0",
                            }}
                          >
                            {takeName(tk, n)}
                          </span>
                          {tk.endFromTakeId && (
                            <span
                              title={t("EndMarker")}
                              style={{
                                position: "relative",
                                zIndex: 1,
                                fontSize: 11,
                                color: "#7c3aed",
                              }}
                            >
                              {t("EndMarker")}
                            </span>
                          )}
                          {active && (
                            <div className="add-video-modal__ai-style-check">
                              <LuCheck size={12} />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </aside>
          )}
        </>
      )}

      {storyOpen && (
        <StoryAssetsPanel
          project={project}
          onClose={onStoryClose}
          cookbook={cookbook}
          busy={busy}
          onAddRef={onRefUrl}
          keyframeShot={
            selectedShot && !isImportShot(selectedShot) && !isContinueShot(selectedShot)
              ? { n: shotIndex.get(selectedShot.id) + 1 }
              : null
          }
          onUseKeyframe={(refId) => {
            onRefKeyframe(selectedShot.id, refId);
            onStoryClose();
          }}
          onNewShot={(prefill) => {
            onStoryClose();
            setAddShot({ prefill });
          }}
          error={error}
          onDismissError={onDismissError}
        />
      )}

      {addShot && (
        <AddShotPanel
          projectId={project.id}
          cast={project.bible?.cast || []}
          locations={project.bible?.locations || []}
          refs={refs}
          keyframes={shots.map((s, i) => ({
            id: s.id,
            item: s.keyframe,
            label: t("Shot", { n: i + 1 }),
          }))}
          places={places}
          defaultPlace={playhead > 0 ? "playhead" : "start"}
          prefill={addShot.prefill}
          models={models}
          model={imageModel}
          onModel={setImageModel}
          videoModel={videoModel}
          onVideoModel={setVideoModel}
          aspect={project.brief?.aspect}
          busy={busy}
          onSubmit={onAddShot}
          onRefUrl={onRefUrl}
          uploading={uploading || busy}
          onUpload={onRefUpload ? uploadRef : undefined}
          onClose={() => setAddShot(null)}
          error={error}
          onDismissError={onDismissError}
        />
      )}

      {finalFrom && (
        <FinalFramePanel
          key={finalFrom.prompt}
          projectId={project.id}
          initialPrompt={finalFrom.prompt}
          source={
            finalFrom.ref ? (
              <RefImage item={finalFrom.ref} />
            ) : (
              <VideoThumb
                url={allTakes.find((tk) => tk.id === finalFrom.takeId)?.url}
                seekSec={(finalFrom.frame + 0.5) / FPS}
                className="add-video-modal__director-frame-thumb"
              />
            )
          }
          models={models}
          model={imageModel}
          onModel={setImageModel}
          busy={busy}
          onClose={() => setFinalFrom(null)}
          error={error}
          onDismissError={onDismissError}
          onGenerate={(prompt) => {
            onRefGenerate({
              prompt,
              model: iModel,
              ...(finalFrom.ref
                ? { sourceRefId: finalFrom.ref.id }
                : { takeId: finalFrom.takeId, frame: finalFrom.frame }),
            });
            setFinalFrom(null);
          }}
        />
      )}

      {nextOpen && (
        <InsertPanel
          projectId={project.id}
          nextRequired={nextRequired}
          canInsert={canInsert}
          anchorTake={anchorTake}
          startFrame={startFrame}
          startLabel={formatTime(playhead)}
          playhead={playhead}
          total={total}
          onNudge={(d) => setPlayhead(clampFrame(playhead + d))}
          afterTake={afterTake}
          afterFrame={after?.frame}
          refs={refs}
          keyframes={shots.map((s, i) => ({
            id: s.id,
            item: s.keyframe,
            label: t("Shot", { n: i + 1 }),
          }))}
          onRefUrl={onRefUrl}
          pickable={pickable}
          models={models}
          model={videoModel}
          onModel={setVideoModel}
          uploading={uploading || busy}
          onUpload={onRefUpload ? uploadRef : undefined}
          onClose={() => setNextOpen(false)}
          error={error}
          onDismissError={onDismissError}
          onGenerate={(text, endFrame) => {
            onContinue(playhead, text, endFrame, vModel);
            setNextOpen(false);
          }}
        />
      )}

      {frameDirect && (
        <DirectorFrameAgent
          key={frameDirect.segmentId + frameDirect.frame}
          anchor={frameDirect}
          proposals={project.proposals}
          imageModel={iModel}
          models={models}
          model={videoModel}
          onModel={setVideoModel}
          onCall={onFrameDirect}
          onClose={() => setFrameDirect(null)}
          error={error}
          onDismissError={onDismissError}
        />
      )}

      {es && (
        <EditShotPanel
          key={es.id}
          projectId={project.id}
          shot={es}
          n={(shotIndex.get(es.id) ?? 0) + 1}
          initialPrompt={editing.prompt}
          renderImages={renderEditImages}
          regen={regen}
          busy={busy}
          onSave={(prompt) => {
            onSaveShot(es.id, prompt);
            setEditing(null);
          }}
          onClose={() => setEditing(null)}
          error={error}
          onDismissError={onDismissError}
        />
      )}
    </div>
  );
}
